import { useState, useEffect, useCallback } from 'react';
import { CROWD_OPERATIONS_CHANGED_EVENT } from './useOperations';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SimulationStatus {
  status:        'running' | 'stopped' | 'unknown';
  scenario:      string;
  tick:          number;
  tickIntervalMs: number;
  zoneCount:     number;
  uptime:        number | null;
  operationMetrics: {
    active:         boolean;
    tickCount:      number;
    operationCount: number;
    zoneStateCount: number;
    flagStateCount: number;
  };
}

export interface SimulationHookState extends SimulationStatus {
  loading: boolean;
  error:   string | null;
  reset:   () => Promise<void>;
  start:   () => Promise<void>;
  stop:    () => Promise<void>;
}

// ─── Anomaly schedule (static — from simulation scenario definition) ──────────

export interface AnomalyEvent {
  tick:        number;
  zone:        string;
  type:        string;
  description: string;
}

export const ANOMALY_SCHEDULE: AnomalyEvent[] = [
  { tick: 45,  zone: 'Main Entrance Gate', type: 'Surge',        description: 'Crowd surge on gates opening (capacity 91%)' },
  { tick: 90,  zone: 'North Stand',        type: 'Overcrowding', description: 'North Stand reaches 88% utilisation' },
  { tick: 120, zone: 'Merchandise Area',   type: 'Surge',        description: 'Pre-match surge at merchandise stalls' },
  { tick: 196, zone: 'Main Entrance Gate', type: 'Surge Spike',  description: 'Gate 3 dwell anomaly — VIP repeated entry detected' },
  { tick: 210, zone: 'Main Entrance Gate', type: 'Decay',        description: 'Post-spike density decay' },
  { tick: 240, zone: 'VIP Area',           type: 'Gathering',    description: 'Restricted gathering growing in VIP section' },
  { tick: 270, zone: 'Emergency Exit',     type: 'Dispersal',    description: 'Dispersal spike through emergency corridor' },
  { tick: 288, zone: 'All zones',          type: 'End',          description: 'Scenario complete — crowd dispersed' },
];

// ─── Mock values ──────────────────────────────────────────────────────────────

const MOCK_STATUS: SimulationStatus = {
  status:         'running',
  scenario:       'large_public_event',
  tick:           144,
  tickIntervalMs: 2000,
  zoneCount:      6,
  uptime:         288000,
  operationMetrics: {
    active:         true,
    tickCount:      144,
    operationCount: 1,
    zoneStateCount: 6,
    flagStateCount: 0,
  },
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 5000;
export const CROWD_SIMULATION_RESET_EVENT = 'vigilo:crowd:simulation-reset';

export function useSimulation(options: { enabled?: boolean } = {}): SimulationHookState {
  const enabled = options.enabled ?? true;
  const [status,  setStatus]  = useState<SimulationStatus>(MOCK_STATUS);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      setError(null);
      return;
    }
    try {
      // Raw fetch through Vite proxy — simulation is not behind JWT
      const [healthRes, statusRes, metricsRes] = await Promise.all([
        fetch('/simulation/health'),
        fetch('/simulation/control/status'),
        fetch('/simulation/control/operation-metrics/status'),
      ]);

      if (!healthRes.ok || !statusRes.ok || !metricsRes.ok) throw new Error('Simulation unreachable');

      const health = await healthRes.json() as { status?: string; scenario?: string; uptimeMs?: number };
      const ctrl   = await statusRes.json() as { active?: boolean; tick?: number; scenario?: string; sessionId?: string };
      const operationMetrics = await metricsRes.json() as SimulationStatus['operationMetrics'];

      setStatus({
        status:         health.status === 'ok' && (ctrl.active || operationMetrics.active) ? 'running' : 'stopped',
        scenario:       ctrl.scenario ?? health.scenario ?? 'unknown',
        tick:           ctrl.tick ?? 0,
        tickIntervalMs: 2000,
        zoneCount:      operationMetrics.zoneStateCount || 6,
        uptime:         typeof health.uptimeMs === 'number' ? health.uptimeMs : null,
        operationMetrics,
      });
      setError(null);
    } catch {
      setStatus(prev => ({ ...prev, status: 'unknown' }));
      setError('Cannot reach simulation server.');
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchStatus();
    const id = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [enabled, fetchStatus]);

  const reset = useCallback(async (): Promise<void> => {
    if (!enabled) return;
    const response = await fetch('/simulation/control/reset', { method: 'POST' });
    if (!response.ok) throw new Error('Failed to reset simulation');
    window.dispatchEvent(new Event(CROWD_SIMULATION_RESET_EVENT));
    window.dispatchEvent(new Event(CROWD_OPERATIONS_CHANGED_EVENT));
    await fetchStatus();
  }, [enabled, fetchStatus]);

  const start = useCallback(async (): Promise<void> => {
    if (!enabled) return;
    const response = await fetch('/simulation/control/operation-metrics/start', { method: 'POST' });
    if (!response.ok) throw new Error('Failed to start operation metrics');
    window.dispatchEvent(new Event(CROWD_SIMULATION_RESET_EVENT));
    await fetchStatus();
  }, [enabled, fetchStatus]);

  const stop = useCallback(async (): Promise<void> => {
    if (!enabled) return;
    const response = await fetch('/simulation/control/operation-metrics/stop', { method: 'POST' });
    if (!response.ok) throw new Error('Failed to stop operation metrics');
    window.dispatchEvent(new Event(CROWD_SIMULATION_RESET_EVENT));
    await fetchStatus();
  }, [enabled, fetchStatus]);

  return { ...status, loading, error, reset, start, stop };
}
